import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import WidgetModel, { NavigateConfig } from '@mm/state/model/WidgetModel';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { ReactNode, useEffect } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { useCreateNewAudience } from '../../../../../Audiences/use-create-new-audience';
import { WithAdderButton } from '../Adder/Adder';

import common from '../index.module.css';
import css from './Condition.module.css';

import { Button } from '@mm/components';
import { ConditionsIcon } from '@mm/icons';
import { TagFilterItem } from '@mm/state/model/AudiencesModel';
import { uniqueId } from 'lodash';
import { MyLeadsFilterBar } from '../../../../../Contacts/MyLeadsFilterBar/MyLeadsFilterBar';
import {
  prepareEsFiltersStructure,
  unassignFilterParams,
} from '../../../../../Contacts/MyLeadsFiltersPanel/helpers';
import { useFilterParams } from '../../../../../Contacts/MyLeadsFiltersPanel/use-filter-params';
import { useCreateFilterParams } from '../../../../../Contacts/use-create-filter-params';
import { useOpenOnReady } from '../use-open-on-ready';
import { ReactComponent as NoPath } from './no.svg';
import { ReactComponent as YesPath } from './yes.svg';

type Props = {
  className?: string;
} & NodeProps<{ widget: WidgetModel; pageGroupId?: number }>;

export const ConditionIcon = ConditionsIcon;

export const Condition: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;

  const config = widget.config as NavigateConfig;

  const audienceId = config.audienceId;
  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, audienceId || null),
  );

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const { createOrEditAudienceAndOpenMenu } = useCreateNewAudience();
  const audiencesCRUDActions = useAudiencesCRUDActions();

  const { filterParams, setFilterParams } = useCreateFilterParams({
    query: prepareEsFiltersStructure(audience?.esFilters) || {},
    filterParams: unassignFilterParams(audience?.filterParams),
    tagsFilters: unassignFilterParams(audience?.tagsFilters) as TagFilterItem[],
    page: 1,
  });
  const { hasFilters, filtersCount: getFiltersCount } = useFilterParams({
    filterParams,
  });

  const filtersCount = getFiltersCount();

  const onClose = (audienceId: number) => {
    if (!widget.config.audienceId && audienceId) {
      try {
        commonWidgetHandlers.update({
          ...widget,
          config: {
            ...config,
            audienceId,
          } as any,
        });
      } catch (err) {}
    }
  };

  const onClick = async () => {
    console.debug(`Condition: onClick: `, { data, widget });

    if (isDragging) {
      return;
    }

    await createOrEditAudienceAndOpenMenu({
      activeAudience: audience,
      filterParams,
      setFilterParams,
      title: 'Condition',
      buttonName: 'Create Condition',
      withSaveAsNew: false,
      hiddenAudience: true,
      onSave: onClose,
    });
  };

  const isNotActive = !hasFilters;

  useOpenOnReady(widget, onClick);

  useEffect(() => {
    console.debug('Condition: useEffect');

    (async () => {
      if (audienceId) {
        await audiencesCRUDActions.retrieveAudienceById(chatbotId, audienceId);
      }
    })();
  }, []);

  return (
    <div className={css.wrapper}>
      <div
        className={classNames(className, css.container, {
          [css.inactive]: isNotActive,
        })}
        onClick={onClick}
      >
        <Handle type="target" position={Position.Top} />

        <div className={classNames(css.body, common.card)}>
          <div className={css.header}>
            <div className={css.icon}>
              <ConditionIcon />
            </div>

            <span className={css.placeholder}>
              {isNotActive
                ? 'Condition is not active'
                : 'User matches following condition?'}
            </span>
          </div>

          <div>
            {React.Children.map<ReactNode, ReactNode>(children, child => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                  onEdit: onClick,
                  key: uniqueId(),
                });
              }
            })}
          </div>

          <div className={css.conditions}>
            {isNotActive && (
              <span className={css.condition}>
                <Button>Add at least 1 filter</Button>
              </span>
            )}

            {filtersCount < 4 ? (
              <div className={css.condition}>
                <MyLeadsFilterBar
                  filterParams={filterParams}
                  setFilterParams={setFilterParams}
                  withReset={false}
                />
              </div>
            ) : (
              <div className={css.conditionsCollapsed}>
                {filtersCount} filter conditions
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <div>
          <YesPath />
          <NoPath />
        </div>

        <div className={css.buttons}>
          <WithAdderButton
            className={classNames(css.path, css.yes)}
            pageId={data.widget.config.pageId}
            sortOrder={0}
            pageGroupId={data.pageGroupId}
            widgetId={data.widget.id}
          >
            <span className={css.response}>Yes</span>
            <Handle id="yes" type="source" position={Position.Bottom} />
          </WithAdderButton>

          <WithAdderButton
            className={classNames(css.path, css.no)}
            pageId={data.widget.config.oppositePageId}
            sortOrder={0}
            pageGroupId={data.pageGroupId}
            widgetId={data.widget.id}
          >
            <span className={css.response}>No</span>
            <Handle id="no" type="source" position={Position.Bottom} />
          </WithAdderButton>
        </div>
      </div>
    </div>
  );
};
